import type { Category } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { ConfirmDialog, Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";

/** Kategoriyalarni boshqarish (faqat admin): qo'shish, nomini o'zgartirish, o'chirish. */
export function CategoriesDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [del, setDel] = useState<Category | null>(null);
  const list = useQuery({
    queryKey: ["categories"],
    queryFn: async () => unwrap(await api.GET("/api/categories/")),
    enabled: open,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["categories"] });
    void qc.invalidateQueries({ queryKey: ["catalog"] });
  };
  const onError = (e: unknown) => toast.error(errorMessage(e, t("common.error")));

  const add = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/categories/", {
          body: { name: name.trim(), order: (list.data?.length ?? 0) + 1 },
        }),
      ),
    onSuccess: () => {
      setName("");
      refresh();
    },
    onError,
  });
  const rename = useMutation({
    mutationFn: async (c: { id: string; name: string }) =>
      unwrap(
        await api.PATCH("/api/categories/{id}/", {
          params: { path: { id: c.id } },
          body: { name: c.name },
        }),
      ),
    onSuccess: refresh,
    onError,
  });
  const remove = useMutation({
    mutationFn: async (c: Category) => {
      const res = await api.DELETE("/api/categories/{id}/", { params: { path: { id: c.id } } });
      if (!res.response.ok) throw new Error("delete");
    },
    onSuccess: () => {
      setDel(null);
      refresh();
    },
    onError,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t("catalog.categories")}>
      <form
        className="mb-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) add.mutate();
        }}
      >
        <Input
          aria-label={t("catalog.category_name")}
          placeholder={t("catalog.category_name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button type="submit" disabled={add.isPending || !name.trim()}>
          {t("catalog.add_category")}
        </Button>
      </form>
      <ul className="divide-y divide-border rounded-lg border border-border">
        {list.data?.map((c) => (
          <li key={c.id} className="flex items-center gap-2 p-2">
            <Input
              aria-label={c.name}
              defaultValue={c.name}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== c.name) rename.mutate({ id: c.id, name: v });
              }}
            />
            <Button
              variant="ghost"
              size="icon"
              aria-label={`${t("common.delete")}: ${c.name}`}
              onClick={() => setDel(c)}
            >
              <Trash2 className="size-4" />
            </Button>
          </li>
        ))}
        {list.data?.length === 0 && (
          <li className="p-3 text-sm text-muted-foreground">{t("catalog.no_categories")}</li>
        )}
      </ul>
      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title={t("catalog.delete_category")}
        message={t("catalog.delete_category_message", { name: del?.name ?? "" })}
        confirmLabel={t("common.delete")}
        pending={remove.isPending}
        onConfirm={() => del && remove.mutate(del)}
      />
    </Dialog>
  );
}
